import { Component, effect, inject, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  ColorSchemeType,
  Colorset,
  colorsetSchemes,
  ColorsetTemplate,
  colorsetTemplates,
  templateByIndex,
} from '../../../models/colorset';
import { ColorsetStore } from '../../../stores/colorset/colorset.state';
import { ButtonModule } from 'primeng/button';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { NgForOf, NgIf } from '@angular/common';
import { ColorPickerModule } from 'primeng/colorpicker';
import { ColorsetService } from '../../../services/colorset/colorset.service';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { AppUtilityService } from '../../../utility/app.utility';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutingService } from '../../../services/routing.service';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { getState } from '@ngrx/signals';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';
import { ContextMenuModule } from 'primeng/contextmenu';
import { ColorsComponent } from '../../atoms/colors/colors.component';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { SplitButtonModule } from 'primeng/splitbutton';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';

@Component({
  selector: 'app-colorset-form',
  standalone: true,
  imports: [
    ButtonModule,
    FormFieldWrapperComponent,
    ReactiveFormsModule,
    InputTextModule,
    DividerModule,
    NgForOf,
    ColorPickerModule,
    FormsModule,
    DropdownModule,
    NgIf,
    ContextMenuModule,
    ColorsComponent,
    FormFooterComponent,
    SplitButtonModule,
    InputGroupModule,
    InputGroupAddonModule,
  ],
  templateUrl: './colorset-form.component.html',
  styleUrl: './colorset-form.component.scss',
})
export class ColorsetFormComponent
  extends BaseFormComponent<Colorset>
  implements OnInit
{
  protected readonly colorsetSchemes = colorsetSchemes;
  protected readonly colorsetTemplates = colorsetTemplates;
  readonly store = inject(ColorsetStore);

  override form: FormGroup = new FormGroup({});
  override itemToEdit?: Colorset;
  override formInitialized = false;
  baseColor = '#ffffff';
  colorScheme: ColorSchemeType = 'complementary';
  numberOfColors = 6;
  contextMenuItems: MenuItem[] | undefined;

  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private fb: FormBuilder,
    private colorsetService: ColorsetService
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );
  }

  override ngOnInit() {
    super.ngOnInit();
  }

  /**
   * Initializes the form for handling item data and prepopulates it based on item-to-edit data or a template.
   * Sets up the form with the appropriate structure and values, including default or existing colors.
   *
   * @return {void} Does not return a value.
   */
  initializeForm(): void {
    this.form = this.fb.group({
      name: [this.itemToEdit?.name || '', Validators.required],
      colors: this.fb.array([]),
    });

    const templateIndex =
      this.routingService.getQueryParamFromRoute('templateIndex');
    const template = templateByIndex(templateIndex);

    // adds start-colors
    if (template) {
      this.addTemplateColors(template);
    } else if (this.itemToEdit) {
      this.itemToEdit.colors.forEach(color => {
        this.addColor(color);
      });
    } else {
      for (let i = 0; i < 6; i++) {
        this.addColor('#ffffff');
      }
    }
    this.formInitialized = true;
  }

  /**
   * Handles the transfer of selected colors from a dropdown to a form.
   * Extracts the selected value from the dropdown change event and adds it to the template colors.
   *
   * @param {DropdownChangeEvent} event - The event triggered by a dropdown value change, containing the selected value.
   * @return {void} No return value.
   */
  copyColorsToForm(event: DropdownChangeEvent): void {
    this.addTemplateColors(event.value);
  }

  /**
   * Adds colors from a provided template to the internal colors form array.
   * Clears the current form array before adding the new colors from the template.
   *
   * @param {ColorsetTemplate} template - The template containing the colors to be added.
   * @return {void} This method does not return any value.
   */
  addTemplateColors(template: ColorsetTemplate): void {
    this.colorsFormArray.clear();
    template.colors.forEach(color => {
      this.addColor(color);
    });
  }

  /**
   * Retrieves the 'colors' field from a reactive form as a FormArray.
   *
   * @return {FormArray} The form field 'colors' cast to a FormArray.
   */
  public get colorsFormArray(): FormArray {
    return this.form.get('colors') as FormArray;
  }

  /**
   * Adds a new color group to the colors form array. If no color is provided, it defaults to '#ffffff'.
   *
   * @param {string} [color] - The hexadecimal value of the color to be added. Default value is '#ffffff'.
   * @return {void} This method does not return anything.
   */
  addColor(color?: string): void {
    const colorGroup = this.fb.group({
      hex: [color || '#ffffff', Validators.required],
    });

    this.colorsFormArray.push(colorGroup);
  }

  /**
   * Removes a color from the colorsFormArray at the specified index.
   *
   * @param {number} index - The index of the color to be removed from the colorsFormArray.
   * @return {void} No return value.
   */
  removeColor(index: number): void {
    this.colorsFormArray.removeAt(index);
  }

  /**
   * Generates a set of colors based on the provided base color, number of colors, and scheme.
   * The method clears existing controls from the colors form array and populates it with the new colors.
   *
   * @return {void} Does not return anything. The method modifies the colors form array directly.
   */
  generateColorset(): void {
    const colors = this.colorsetService.generateColorScheme(
      this.baseColor,
      this.numberOfColors,
      this.colorScheme
    );

    this.colorsFormArray.controls = [];
    colors.forEach((color: string) => {
      this.addColor(color);
    });
  }

  /**
   * Handles the form submission by validating the form and processing it accordingly.
   * If the form is valid, it constructs a `Colorset` object with the form values
   * and the hex color values from the form array, and submits it.
   * If the form is invalid, it marks all fields as touched and dirty for validation feedback.
   *
   * @return {void} Does not return a value.
   */
  onSubmit(): void {
    if (this.form.valid) {
      const colorset: Colorset = {
        ...this.form.value,
        ...{
          colors: this.colorsFormArray.value.map((item: { hex: string }) => {
            return item.hex;
          }),
        },
      };

      this.submit(colorset);
    } else {
      this.form.markAllAsTouched();
      this.appUtilityService.markAllAsDirty(this.form);
    }
  }

  /**
   * Sets the context menu items for a specific index.
   *
   * @param {number} index - The index of the item for which the context menu is being set.
   * @return {void} This method does not return any value.
   */
  setContextMenuItems(index: number): void {
    this.contextMenuItems = [
      {
        label: 'löschen',
        icon: 'fas fa-trash',
        command: (event: MenuItemCommandEvent) => {
          this.removeColor(index);
        },
      },
    ];
  }

  /**
   * Retrieves a set of template options for colors, including labels, icons, and associated items.
   *
   * @return {Array<Object>} An array of objects where each object contains a label, icon, and an array of items.
   */
  get colorsetTemplateOptions() {
    return [
      {
        label: 'Vorlagen',
        icon: 'fas fa-pen-ruler',
        items: this.colorsetTemplates,
      },
      {
        label: 'Kommunenfarbsets',
        icon: 'fas fa-palette',
        items: this.store.asArray(),
      },
    ];
  }

  /**
   * Retrieves a list of colors used along with their corresponding label.
   * Each color is represented as an object containing a label and the color value.
   *
   * @return {Array<Object>} An array of objects where each object has a `label` property set to 'Vorlagen' and a `color` property containing the color value.
   */
  get usedColors() {
    return this.store.allColors().map(color => {
      return { label: 'Vorlagen', color: color };
    });
  }
}
